import { useEffect } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { Carousel, CarouselProps } from "@/components/confluence/carousel/src";
import { AnimatedJiraTour } from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import SignupModule, { Bundle } from "@/components/signup/bxp-ui-signup/src";
import { Edition } from "@/components/signup/bxp-ui-signup/src";
import { SignInProviders } from "@/components/signup/bxp-ui-signup/src/components/signup-form/components";
import { Picture, PictureImageProps } from "@/components/wpl/image/src";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";
import {
    AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY,
    sendConversionEventAlgolia
} from "@/patterns/global-nav/src/auto-complete-helpers";
import { SignupPageTemplateBaseProps } from "@/templates/try/cloud/signup/signup-page/src";
import { ClientOnly } from "@/utils/client-only";
import { getLocalStorage } from "@/utils/localstorage";

const contentStyle = css({
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto",
    width: "100%",

    [media.below.md]: {
        flexDirection: "column"
    }
});

const formColumn = css({
    display: "flex",
    flexDirection: "column",
    [media.above.md]: {
        maxWidth: "500px",
        width: "44%"
    },
    [media.below.md]: {
        alignItems: "center"
    }
});

const signupFormContainer = css({
    maxWidth: "368px",
    width: "100%"
});

const headerStyle = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "3rem",
    lineHeight: "3.5rem",
    fontWeight: 500,
    marginBlockStart: 50,
    marginBlockEnd: "24px",

    [media.below.md]: {
        textAlign: "center"
    }
});

const imgContainer = css({
    maxWidth: "650px",
    [media.above.md]: {
        width: "56%"
    },
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    }
});

const carouselContainerStyles = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "700px",
    height: "600px",

    [media.below.sm]: {
        width: "100%",
        height: "100%"
    }
});

const subHeaderStyles = css({
    marginBlockEnd: "16px",
    textAlign: "center",
    [media.above.sm]: {
        textAlign: "left"
    }
});

const animationHeroContainer = css({
    position: "relative",
    aspectRatio: "800 / 550",
    height: "auto",
    minHeight: "1px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [media.below.lg]: {
        maxWidth: "100%"
    },
    [media.below.md]: {
        display: "none"
    }
});

const staticImageContainer = css({
    display: "none",
    [media.below.md]: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column"
    }
});

export interface PaidHeroProps {
    readonly header: string;
    readonly subHeader?: string;
    readonly image?: PictureImageProps;
    readonly theme?: string;
    readonly animatedSocialBanner?: boolean;
    readonly showAnimatedHero?: boolean;
    readonly showStaticImage?: boolean;
    readonly carouselHeroProps?: CarouselProps;
    readonly useNewHero?: boolean;
    readonly heroImageSubheader?: string;
    readonly hasRebrandedSignupFormStyles?: boolean;
    readonly signup?: SignupPageTemplateBaseProps;
    readonly showKeywordAnimation?: boolean;
    readonly heroBgColor?: BgColors;
    readonly signupEdition?: Edition;
    readonly customSignInProviders?: SignInProviders;
    readonly hideLegalText?: boolean;
    readonly isTreatment?: boolean;
}

export const PaidHeroTabPanelTemplate = ({
    header,
    subHeader,
    image,
    animatedSocialBanner,
    showAnimatedHero = false,
    carouselHeroProps,
    hasRebrandedSignupFormStyles = true,
    signup,
    heroBgColor = "bgWhite",
    signupEdition,
    customSignInProviders,
    hideLegalText
}: PaidHeroProps) => {
    const productBundle = (signup?.bundle || "jira-software") as Bundle;
    useEffect(() => {
        const getaaCompleteQueryObj = getLocalStorage(
            AA_AUTOCOMPLETE_POTENTIAL_CONVERSION_EVENT_LOCAL_STORAGE_KEY
        );
        if (getaaCompleteQueryObj) {
            const parsedStringifyAttributes = JSON.parse(getaaCompleteQueryObj);
            sendConversionEventAlgolia({
                objectID: parsedStringifyAttributes.objectID,
                queryID: parsedStringifyAttributes.queryID,
                position: parsedStringifyAttributes.position
            });
        }
    }, []);

    return (
        <Row>
            <div css={[contentStyle]} data-testid="hero-tab-panel">
                <div css={[formColumn]}>
                    <div>
                        <h1 css={[headerStyle]}>{header}</h1>
                    </div>
                    {subHeader && (
                        <span css={subHeaderStyles}>{subHeader}</span>
                    )}
                    <div css={signupFormContainer} data-testid="signup-form">
                        <ClientOnly>
                            <SignupModule
                                bundle={productBundle}
                                edition={signupEdition || "free"}
                                emailFormOnly={true}
                                hasRebrandedSignupFormStyles={
                                    hasRebrandedSignupFormStyles
                                }
                                // bandaid fix to be addressed with tech-debt ticket https://hello.jira.atlassian.cloud/browse/JFIND-58
                                animatedSocialBanner={
                                    animatedSocialBanner === undefined
                                        ? true
                                        : animatedSocialBanner
                                }
                                bgColor={heroBgColor}
                                customSignInProviders={customSignInProviders}
                                hideLegalText={hideLegalText}
                            />
                        </ClientOnly>
                    </div>
                </div>
                {showAnimatedHero && (
                    <div css={imgContainer}>
                        <div css={animationHeroContainer}>
                            <AnimatedJiraTour />
                        </div>
                    </div>
                )}
                {carouselHeroProps ? (
                    <div css={carouselContainerStyles}>
                        <Carousel {...carouselHeroProps} />
                    </div>
                ) : (
                    image && (
                        <div
                            css={[
                                showAnimatedHero
                                    ? staticImageContainer
                                    : imgContainer
                            ]}
                        >
                            {image && (
                                <Picture
                                    image={image}
                                    style={{
                                        maxWidth: "100%",
                                        width: "100%",
                                        height: "auto"
                                    }}
                                />
                            )}
                        </div>
                    )
                )}
            </div>
        </Row>
    );
};

PaidHeroTabPanelTemplate.displayName = "PaidHeroTemplate";
